import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { fetchOnboardingSettings, updateOnboardingSettings } from '../../../api/adminOnboardingSettings';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { Spinner } from '../../../components/ui/spinner';
import { Switch } from '../../../components/ui/switch';
import { useNotification } from '../../../context/NotificationProvider';
import { notifyMutationError } from '../../../lib/errors';

export function WhitelistSettingsCard() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'onboarding-settings'],
    queryFn: fetchOnboardingSettings,
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => updateOnboardingSettings({ whitelist_enabled: enabled }),
    onSuccess: (result) => {
      queryClient.setQueryData(['admin', 'onboarding-settings'], result);
      showSuccess(
        result.whitelist_enabled
          ? 'Verificação de whitelist ativada.'
          : 'Verificação de whitelist desativada.',
      );
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <Card>
      <CardContent className="p-5 flex items-start gap-4">
        <div className="rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)] p-3">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              Verificação de whitelist
            </h3>
            {data && (
              <Badge variant="secondary" className="text-[11px]">
                {data.source === 'admin' ? 'Sobrescrito por admin' : 'Padrão do ambiente'}
              </Badge>
            )}
          </div>
          <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
            Quando ativa, o onboarding só é liberado para emails previamente cadastrados na whitelist.
            Os demais são enviados para a tela de acesso por convite. Pode ser desligado no hard open
            do sistema sem necessidade de deploy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isLoading || toggleMutation.isPending ? (
            <Spinner size={16} />
          ) : (
            <Switch
              checked={data?.whitelist_enabled ?? false}
              onCheckedChange={(checked) => toggleMutation.mutate(Boolean(checked))}
              aria-label="Verificação de whitelist ativa"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
